export const M6_UNIT_12 = {
  id: "m6-u12-tag-questions-global",
  title: "Unidad 12: Tag Questions & Global English",
  topic: "Master conversation tags and navigate English worldwide! Learn formal vs casual language, understand UK/US/Canada/Australia differences. Speak confidently anywhere!",
  iconType: "globe-americas",
  grammarImage: "/grammar/grammar-m6-u12.jpg",
  grammarText: "Tag questions (aren't you? / don't they?). Register awareness (formal vs casual). Global English varieties (US 🇺🇸 UK 🇬🇧 Canada 🇨🇦 Australia 🇦🇺). Cultural communication styles!",
  sets: [

    // ===================================================================
    // SET 1: TAG QUESTIONS BASICS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s1",
      title: "Set 1: Tag Questions Foundation",
      questions: [
        {
          type: "match",
          q: "Match tag question patterns",
          pairs: [
            { left: "Positive statement", right: "Negative tag ✓" },
            { left: "Negative statement", right: "Positive tag ✓" },
            { left: "Real question", right: "Rising intonation ↗️" }
          ],
          a: "completed",
          successtip: "✓TAG QUESTIONS!\nTag = mini question at end 🏷️\nPositive → negative tag ✓\nNegative → positive tag ✓\nVery British! 🇬🇧☕"
        },
        {
          type: "mc",
          q: "'You're a student, ___ you?'",
          options: ["aren't", "are", "isn't"],
          a: "aren't",
          tip: "Positive (you're) → negative tag (aren't)",
          successtip: "✓AREN'T YOU!\nYou're → aren't you? ✓\nPositive statement = negative tag\nBasic pattern 💯\nBritish say this ALL the time 🇬🇧"
        },
        {
          type: "order",
          q: "Context: Confirmando algo obvio. Traduce: Hace frío hoy, ¿verdad?",
          words: ["It's", "cold", "today,", "isn't", "it?"],
          a: "It's cold today, isn't it?",
          tip: "It's → isn't it?",
          successtip: "✓ISN'T IT!\nWeather talk = British specialty ☁️\n'Isn't it?' = ¿verdad? 🤔\nSmall talk classic 💬"
        },
        {
          type: "input",
          q: "'She doesn't speak French, ___ she?'",
          a: "does",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓DOES SHE!\nNegative (doesn't) → positive tag (does) ✓\nFlip the polarity!\nPattern recognition = key 🔑"
        },
        {
          type: "mc",
          q: "Tag question shows:",
          options: ["You think you know, checking confirmation", "You have no idea", "You're making a statement"],
          a: "You think you know, checking confirmation",
          successtip: "✓PURPOSE!\nTag = confirm what you believe 🤔\n'Right?' in US = same thing 🇺🇸\nSeeking agreement 👍\nConversation tool 💬"
        },
        {
          type: "order",
          q: "Context: Pregunta confirmando. Traduce: Has estado allí, ¿verdad?",
          words: ["You've", "been", "there,", "haven't", "you?"],
          a: "You've been there, haven't you?",
          tip: "You've → haven't you?",
          successtip: "✓HAVEN'T YOU!\nPresent Perfect + tag ✓\nYou've → haven't you?\nSame auxiliary verb! 🎯"
        },
        {
          type: "mc",
          q: "Americans often replace tag questions with:",
          options: ["'right?'", "'correct?'", "'yes?'"],
          a: "'right?'",
          successtip: "✓RIGHT!\n🇬🇧 UK: 'isn't it?' (proper tag)\n🇺🇸 US: 'right?' (easier!) 😅\n🇨🇦 Canada: both + 'eh?'\nAll work! ✓"
        },
        {
          type: "input",
          q: "'They can swim, ___ they?'",
          a: "can't",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓CAN'T THEY!\nCan → can't ✓\nModals follow same rule\nSame modal in tag 🎯"
        },
        {
          type: "mc",
          q: "Intonation difference: falling ↘️ vs rising ↗️",
          options: ["Falling = confirming. Rising = real question.", "Both same meaning", "Falling = question. Rising = statement."],
          a: "Falling = confirming. Rising = real question.",
          successtip: "✓INTONATION!\n↘️ Falling = pretty sure (confirming)\n↗️ Rising = genuinely asking (uncertain)\nChanges meaning! 🎵\nBut don't stress it 😌"
        },
        {
          type: "match",
          q: "Match statements to tags",
          pairs: [
            { left: "You like pizza", right: "don't you?" },
            { left: "He can't drive", right: "can he?" },
            { left: "They're coming", right: "aren't they?" }
          ],
          a: "completed",
          successtip: "✓PATTERN MASTERY!\nPositive → negative ✓\nNegative → positive ✓\nSame verb/modal ✓\nYou've got this! 💪"
        }
      ]
    },

    // ===================================================================
    // SET 2: COMMON TAG QUESTIONS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s2",
      title: "Set 2: Tag Questions Practice",
      questions: [
        {
          type: "mc",
          q: "'She's your sister, ___ she?'",
          options: ["isn't", "is", "doesn't"],
          a: "isn't",
          tip: "She's = she is → isn't",
          successtip: "✓ISN'T!\nShe's → isn't she? ✓\nContraction = 'is' (not 'does')\nWatch for 's = is or has! 👀"
        },
        {
          type: "order",
          q: "Context: Confirmando planes. Traduce: Vienen mañana, ¿verdad?",
          words: ["They're", "coming", "tomorrow,", "aren't", "they?"],
          a: "They're coming tomorrow, aren't they?",
          successtip: "✓PLANS!\nFuture with present continuous ✓\nThey're coming → aren't they?\nCommon tag for plans 📅"
        },
        {
          type: "input",
          q: "'You won't be late, ___ you?'",
          a: "will",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓WON'T → WILL!\nWon't (negative) → will (positive) ✓\nFuture tag questions\nParent question clásica 😅"
        },
        {
          type: "mc",
          q: "'He has finished, ___ he?'",
          options: ["hasn't", "isn't", "doesn't"],
          a: "hasn't",
          tip: "Has (auxiliary) → hasn't",
          successtip: "✓HAS → HASN'T!\nPresent Perfect tag ✓\nHe has → hasn't he?\n'Has' = auxiliary here 🎯"
        },
        {
          type: "order",
          q: "Context: Asumiendo negativo. Traduce: No habla español, ¿verdad?",
          words: ["He", "doesn't", "speak", "Spanish,", "does", "he?"],
          a: "He doesn't speak Spanish, does he?",
          tip: "Doesn't → does",
          successtip: "✓NEGATIVE → POSITIVE!\nDoesn't → does he? ✓\nExpecting 'no' answer\nTag confirms negative 👎"
        },
        {
          type: "mc",
          q: "'Let's go, ___ we?'",
          options: ["shall", "will", "do"],
          a: "shall",
          tip: "LET'S = special tag (shall we)",
          successtip: "✓LET'S → SHALL WE!\nSpecial case! 🎯\n'Let's go, shall we?' = British\n'Let's go, OK?' = American 🇺🇸\nBoth invitations ✓"
        },
        {
          type: "input",
          q: "'There's a problem, ___ there?'",
          a: "isn't",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓THERE'S!\nThere's → isn't there? ✓\nThere are → aren't there? ✓\n'There' tags = tricky! 🪤"
        },
        {
          type: "mc",
          q: "'You can help me, ___ you?'",
          options: ["can't", "don't", "aren't"],
          a: "can't",
          successtip: "✓CAN → CAN'T!\nModals = easy tags ✓\nCan → can't\nCould → couldn't\nWould → wouldn't\nSame modal! 🎯"
        },
        {
          type: "order",
          q: "Context: Confirmando memoria. Traduce: Nos conocimos antes, ¿verdad?",
          words: ["We've", "met", "before,", "haven't", "we?"],
          a: "We've met before, haven't we?",
          successtip: "✓MET BEFORE!\nWe've → haven't we? ✓\nRecognition question\nAwkward moment preventer 😅"
        },
        {
          type: "match",
          q: "Match tricky tags",
          pairs: [
            { left: "I'm right", right: "aren't I?" },
            { left: "Let's eat", right: "shall we?" },
            { left: "There's time", right: "isn't there?" }
          ],
          a: "completed",
          successtip: "✓SPECIAL CASES!\n'I'm' → 'aren't I?' (weird but correct! 🤷)\n'Let's' → 'shall we?' (invitation)\n'There's' → 'isn't there?' (existence)\nMemorize these! 📝"
        }
      ]
    },

    // ===================================================================
    // SET 3: FORMAL vs INFORMAL INTRO (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s3",
      title: "Set 3: Register Awareness",
      questions: [
        {
          type: "match",
          q: "Match register to context",
          pairs: [
            { left: "Formal 🎩", right: "Job interview, boss, client" },
            { left: "Neutral 👔", right: "Coworkers, acquaintances" },
            { left: "Casual 👕", right: "Friends, family, text" }
          ],
          a: "completed",
          successtip: "✓REGISTER!\nRegister = nivel de formalidad 🎚️\nContext determines choice\nRead the room! 🎯\nWhen unsure → formal 🎩"
        },
        {
          type: "mc",
          q: "Job interview greeting:",
          options: ["Good morning. Thank you for meeting with me.", "Hey! What's up?", "Yo! Thanks for having me!"],
          a: "Good morning. Thank you for meeting with me.",
          tip: "Formal context = formal language",
          successtip: "✓FORMAL!\nInterview = formal always 🎩\n'Good morning' ✓\n'Hey' ❌ (too casual)\nFirst impressions matter! 💼"
        },
        {
          type: "order",
          q: "Context: Email formal a jefe. Traduce: Me gustaría solicitar un día libre.",
          words: ["I", "would", "like", "to", "request", "a", "day", "off."],
          a: "I would like to request a day off.",
          tip: "WOULD LIKE + REQUEST = formal",
          successtip: "✓FORMAL REQUEST!\n'I would like to request...' 🎩\nVs casual: 'Can I take...' 👕\nBoss = formal language 💼"
        },
        {
          type: "input",
          q: "Friend texting: 'Wanna ___ out tonight?' (hang)",
          a: "hang",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓WANNA!\nWanna = want to (casual) 👕\nHang out = spend time 🎮\nText speak = super casual 📱\nNEVER in formal writing! ❌"
        },
        {
          type: "mc",
          q: "Email to professor you've never met:",
          options: ["Dear Professor Smith,", "Hey Prof!", "Hi there!"],
          a: "Dear Professor Smith,",
          successtip: "✓DEAR!\n🎩 Formal: Dear Professor/Dr./Mr./Ms.\n👔 Neutral: Hello Professor Smith,\n👕 Casual: Hey! (friends only)\nAcademic = formal 🎓"
        },
        {
          type: "order",
          q: "Context: Respuesta casual a amigo. Traduce: ¡Suena genial!",
          words: ["Sounds", "great!"],
          a: "Sounds great!",
          tip: "Casual = short and simple",
          successtip: "✓CASUAL!\nSounds great! 👍\nNo full sentences needed\nEmojis OK 😊\nFriends = relaxed 👕"
        },
        {
          type: "mc",
          q: "Formal vs casual request:",
          options: ["'Could you please...' = formal. 'Can you...' = casual.", "Both exactly the same", "'Can' = formal. 'Could' = casual."],
          a: "'Could you please...' = formal. 'Can you...' = casual.",
          successtip: "✓POLITENESS SCALE!\n🎩 Could you please...? (most formal)\n👔 Could you...? (polite)\n👕 Can you...? (casual)\n😎 Get me... (very casual/rude)\nContext matters! 🎯"
        },
        {
          type: "input",
          q: "Casual goodbye to friend: 'See ___!' (ya)",
          a: "ya",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["you"] },
          successtip: "✓SEE YA!\nSee ya! = See you! 👋\nCasual pronunciation spelling\n'Ya' = 'you' (casual) 👕\nTexts/speech only ❌ formal writing"
        },
        {
          type: "mc",
          q: "Which is TOO casual for work email?",
          options: ["'Hey! Got your msg. Will do ASAP. Thx!'", "'Hello, I received your message and will complete it soon. Thank you.'", "'Hi, thanks for the message. I'll get that done soon.'"],
          a: "'Hey! Got your msg. Will do ASAP. Thx!'",
          successtip: "✓TOO CASUAL!\nWork = at least neutral 👔\nAbbreviations = texts only 📱\n'Msg/Thx/ASAP' = too casual ❌\nUnless company culture very casual 🤷"
        },
        {
          type: "match",
          q: "Match formality levels",
          pairs: [
            { left: "Thank you very much", right: "Formal 🎩" },
            { left: "Thanks!", right: "Neutral 👔" },
            { left: "Thx/Ty", right: "Very casual 😎" }
          ],
          a: "completed",
          successtip: "✓THANKS SCALE!\n🎩 Thank you very much for...\n👔 Thanks! / Thank you!\n👕 Thanks a lot! / Thanks a ton!\n😎 Thx! / Ty! (text only)\nMirror their level! 🪞"
        }
      ]
    },

    // ===================================================================
    // SET 4: FORMAL REGISTER - PROFESSIONAL (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s4",
      title: "Set 4: Professional English",
      questions: [
        {
          type: "match",
          q: "Match formal professional phrases",
          pairs: [
            { left: "Opening", right: "Dear Sir/Madam," },
            { left: "Request", right: "I would appreciate..." },
            { left: "Closing", right: "Kind regards," }
          ],
          a: "completed",
          successtip: "✓FORMAL EMAIL!\nStructure = important 📧\nDear... → Body → Kind regards/Sincerely\nProfessional image 💼"
        },
        {
          type: "mc",
          q: "Formal way to say 'I want to ask about...':",
          options: ["I would like to inquire about...", "I wanna know about...", "Tell me about..."],
          a: "I would like to inquire about...",
          tip: "INQUIRE = formal 'ask'",
          successtip: "✓INQUIRE!\nInquire = preguntar (formal) 🎩\nVs 'ask' = neutral 👔\nVs 'wanna know' = casual 👕\nBusiness vocabulary 💼"
        },
        {
          type: "order",
          q: "Context: Email formal agradeciendo. Traduce: Gracias por su tiempo y consideración.",
          words: ["Thank", "you", "for", "your", "time", "and", "consideration."],
          a: "Thank you for your time and consideration.",
          tip: "Formal closing phrase",
          successtip: "✓CONSIDERATION!\nStandard formal closing 🎩\nJob applications love this 💼\nShows professionalism ✓"
        },
        {
          type: "input",
          q: "Formal: 'I ___ your assistance.' (appreciate)",
          a: "appreciate",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓APPRECIATE!\nAppreciate = agradecer (formal) 🎩\nVs 'Thanks for' = casual 👕\n'I appreciate' = professional tone 💼"
        },
        {
          type: "mc",
          q: "Polite way to check availability:",
          options: ["Would you be available to meet?", "Can you meet?", "Wanna meet?"],
          a: "Would you be available to meet?",
          successtip: "✓AVAILABLE!\n'Would you be available...?' = very polite 🎩\nGives them easy out\nRespectful of time ⏰\nBusiness meetings 💼"
        },
        {
          type: "order",
          q: "Context: Email profesional informando. Traduce: Le escribo para informarle que...",
          words: ["I'm", "writing", "to", "inform", "you", "that..."],
          a: "I'm writing to inform you that...",
          tip: "Formal email opening",
          successtip: "✓WRITING TO!\n'I'm writing to...' = formal opening 📧\nClear purpose statement\nProfessional communication 💼"
        },
        {
          type: "mc",
          q: "Formal way to say 'as soon as possible':",
          options: ["at your earliest convenience", "ASAP", "really soon"],
          a: "at your earliest convenience",
          successtip: "✓CONVENIENCE!\n🎩 At your earliest convenience (formal)\n👔 When you can (neutral)\n👕 ASAP (casual)\n😎 Hurry up! (rude! ❌)"
        },
        {
          type: "input",
          q: "Formal email closing: 'Kind ___,' or 'Best ___,'",
          a: "regards",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓REGARDS!\nKind regards = formal 🎩\nBest regards = formal 🎩\nRegards = neutral 👔\nBest = casual 👕\nCheers = British casual 🇬🇧"
        },
        {
          type: "mc",
          q: "Saying 'no' professionally:",
          options: ["I'm afraid I'm unable to...", "Nope, can't do it.", "No way!"],
          a: "I'm afraid I'm unable to...",
          successtip: "✓I'M AFRAID!\n'I'm afraid...' = softens bad news 🎩\nVery British 🇬🇧\nProfessional rejection 💼\nBetter than direct 'no' ❌"
        },
        {
          type: "match",
          q: "Match formal vs casual",
          pairs: [
            { left: "Formal: request", right: "I would like to request..." },
            { left: "Casual: request", right: "Can I get...?" },
            { left: "Very casual", right: "Gimme..." }
          ],
          a: "completed",
          successtip: "✓NEVER 'GIMME'!\n'Gimme' = give me (super casual/rude) 😬\nWork = never use ❌\nFriends = maybe OK 🤷\nSafety = formal language 🎩"
        }
      ]
    },

    // ===================================================================
    // SET 5: DIALOGUE - FORMAL TO CASUAL NAVIGATION (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s5",
      title: "Set 5: Adjusting Your Register",
      questions: [
        {
          type: "mc",
          q: "DAY 1: First day at new job. Boss: 'Welcome!' YOU: '___'",
          options: ["Thank you so much for the opportunity.", "Thanks! Excited to be here!", "Hey! Let's do this!"],
          a: "Thank you so much for the opportunity.",
          tip: "First day = formal",
          successtip: "✓FIRST DAY!\nStart formal 🎩\nCan go casual later ✓\nCan't go back to formal ❌\nSafe strategy! 💼"
        },
        {
          type: "order",
          q: "DAY 1: Asking where bathroom is (formal). Traduce: Disculpe, ¿dónde está el baño?",
          words: ["Excuse", "me,", "where", "is", "the", "restroom?"],
          a: "Excuse me, where is the restroom?",
          tip: "Restroom = formal word for bathroom",
          successtip: "✓RESTROOM!\n🎩 Restroom (formal)\n👔 Bathroom (neutral)\n👕 Toilet (British/casual)\n🇺🇸 US: restroom/bathroom\n🇬🇧 UK: toilet/loo"
        },
        {
          type: "input",
          q: "WEEK 2: Getting comfortable. Coworker: 'Wanna grab lunch?' YOU: 'Sure, ___ great!' (sounds)",
          a: "sounds",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓SOUNDS GREAT!\nMirroring their casualness 🪞\nThey said 'wanna' → you can be casual 👕\nBuilding rapport 🤝"
        },
        {
          type: "mc",
          q: "MONTH 1: Boss says 'Call me Mike' (not Mr. Smith). This means:",
          options: ["You can be more casual with him", "Still use Mr. Smith", "Call him dude"],
          a: "You can be more casual with him",
          successtip: "✓FIRST NAME!\nFirst name = permission to be casual ✓\nCommon in US/Australia 🇺🇸🇦🇺\nStill professional at work 👔\nNot 'dude' 😂"
        },
        {
          type: "order",
          q: "MONTH 2: Casual email to friendly coworker. Traduce: Oye, ¿tienes un minuto?",
          words: ["Hey,", "do", "you", "have", "a", "minute?"],
          a: "Hey, do you have a minute?",
          tip: "Established relationship = casual OK",
          successtip: "✓HEY!\n'Hey' = casual greeting 👕\nWith work friends = OK ✓\nStill not with boss 🎩\nJudge relationship! 🤔"
        },
        {
          type: "mc",
          q: "MONTH 3: Meeting new client. Back to:",
          options: ["Formal (they don't know you yet)", "Casual (you're comfortable now)", "Super casual (you're confident)"],
          a: "Formal (they don't know you yet)",
          successtip: "✓RESET!\nNew person = reset to formal 🔄\nEven if you're comfortable at company\nThey need to trust you first 🤝\nContext switching! 🎯"
        },
        {
          type: "input",
          q: "MONTH 6: Text to work friend: 'Running late! ___ ya soon!' (see)",
          a: "See",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓TEXT CASUAL!\nWork friends = can text casually 📱\n'See ya' = totally OK 👕\nStill professional in meetings 💼\nCompartmentalize! 🎯"
        },
        {
          type: "mc",
          q: "Rule of thumb for register:",
          options: ["Mirror their level, but start formal when unsure", "Always be super formal", "Always be super casual"],
          a: "Mirror their level, but start formal when unsure",
          successtip: "✓MIRROR!\nMirror = copy their style 🪞\nThey formal → you formal 🎩\nThey casual → you can go casual 👕\nSocial intelligence! 🧠\nB2 skill! ✨"
        },
        {
          type: "order",
          q: "EMAIL vs TEXT: Formal request. Traduce: ¿Podría enviarme ese archivo?",
          words: ["Could", "you", "send", "me", "that", "file?"],
          a: "Could you send me that file?",
          tip: "Email = more formal than text",
          successtip: "✓MEDIUM MATTERS!\n📧 Email = more formal 👔\n📱 Text/Slack = more casual 👕\n💬 In person = read the room 🎯\nSame person, different register! 🎚️"
        },
        {
          type: "mc",
          q: "FINAL: You've learned to:",
          options: ["Adjust formality based on context, relationship, and medium", "Always stay formal", "Always be casual"],
          a: "Adjust formality based on context, relationship, and medium",
          successtip: "✓FLEXIBILITY!\nRegister flexibility = B2 skill 🎯\nContext 🏢, relationship 🤝, medium 📧\nNo one-size-fits-all ❌\nAdaptability = professionalism 💪\n\nYou've got this! 🌟"
        }
      ]
    },

    // ===================================================================
    // SET 6: UK vs US VOCABULARY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s6",
      title: "Set 6: UK 🇬🇧 vs US 🇺🇸 Words",
      questions: [
        {
          type: "match",
          q: "Match UK 🇬🇧 to US 🇺🇸 vocabulary",
          pairs: [
            { left: "🇬🇧 Flat", right: "🇺🇸 Apartment" },
            { left: "🇬🇧 Lift", right: "🇺🇸 Elevator" },
            { left: "🇬🇧 Lorry", right: "🇺🇸 Truck" }
          ],
          a: "completed",
          successtip: "✓SAME LANGUAGE!\n🇬🇧🇺🇸 Different words, same meaning\nBoth understand both ✓\nBut use their own 🎯\nNo right/wrong! Just different 🌍"
        },
        {
          type: "mc",
          q: "🇬🇧 'Rubbish' = 🇺🇸 ___",
          options: ["Trash / Garbage", "Waste", "Junk"],
          a: "Trash / Garbage",
          tip: "Different words for same thing",
          successtip: "✓RUBBISH!\n🇬🇧 Rubbish bin\n🇺🇸 Trash can / Garbage can\n🇦🇺 Rubbish bin (follows UK)\n🇨🇦 Garbage (follows US)"
        },
        {
          type: "order",
          q: "Context: 🇬🇧 Brit describing waiting. Traduce (UK): Estamos haciendo fila.",
          words: ["We're", "queuing."],
          a: "We're queuing.",
          tip: "🇬🇧 Queue = 🇺🇸 Line",
          successtip: "✓QUEUE!\n🇬🇧 Queue (sacred!) 🚶‍♂️🚶‍♀️\n🇺🇸 Line / Stand in line\nBritish queue culture = serious! 😤\nCutting queue = crime 🚫"
        },
        {
          type: "input",
          q: "🇬🇧 'Trousers' = 🇺🇸 ___",
          a: "Pants",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓PANTS TRAP! 🚨\n🇬🇧 Pants = UNDERWEAR 🩲\n🇬🇧 Trousers = pants 👖\n🇺🇸 Pants = trousers 👖\n🇺🇸 Underwear = underwear 🩲\n\nDon't say 'nice pants' to Brit! 😂"
        },
        {
          type: "mc",
          q: "🇬🇧 'Biscuit' = 🇺🇸 ___",
          options: ["Cookie", "Cracker", "Bread"],
          a: "Cookie",
          successtip: "✓BISCUIT!\n🇬🇧 Biscuit = sweet cookie 🍪\n🇺🇸 Biscuit = savory bread 🥐\n🇺🇸 Cookie = sweet cookie 🍪\nConfusing! 😅"
        },
        {
          type: "order",
          q: "Context: 🇺🇸 American ordering. Traduce (US): Quiero papas fritas.",
          words: ["I", "want", "fries."],
          a: "I want fries.",
          tip: "🇺🇸 Fries = 🇬🇧 Chips",
          successtip: "✓FRIES vs CHIPS!\n🇬🇧 Chips = french fries 🍟\n🇬🇧 Crisps = potato chips\n🇺🇸 Fries = french fries 🍟\n🇺🇸 Chips = potato chips\nFlipped! 🔄"
        },
        {
          type: "mc",
          q: "🇬🇧 'Holiday' = 🇺🇸 ___",
          options: ["Vacation", "Day off", "Trip"],
          a: "Vacation",
          successtip: "✓HOLIDAY!\n🇬🇧 Holiday = vacation 🏖️\n🇺🇸 Holiday = Christmas/Easter/etc 🎄\n🇺🇸 Vacation = time off 🏖️\nDifferent meanings! 🎯"
        },
        {
          type: "input",
          q: "🇬🇧 'Mobile' = 🇺🇸 ___ phone",
          a: "Cell",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["cellular"] },
          successtip: "✓PHONE!\n🇬🇧 Mobile (phone)\n🇺🇸 Cell phone / Cellular\n🇦🇺 Mobile\n🇨🇦 Cell phone\nShort = just 'phone' everywhere 📱"
        },
        {
          type: "mc",
          q: "🇬🇧 'Football' vs 🇺🇸 'Soccer':",
          options: ["Same sport, different names ⚽", "Different sports", "Same name everywhere"],
          a: "Same sport, different names ⚽",
          successtip: "✓FOOTBALL!\n🇬🇧🌍 Rest of world: Football ⚽\n🇺🇸 US: Soccer ⚽\n🇺🇸 US Football: 🏈 (different sport!)\nCauses confusion globally 😅"
        },
        {
          type: "match",
          q: "Match more UK 🇬🇧 vs US 🇺🇸",
          pairs: [
            { left: "🇬🇧 Boot (car)", right: "🇺🇸 Trunk" },
            { left: "🇬🇧 Bonnet (car)", right: "🇺🇸 Hood" },
            { left: "🇬🇧 Petrol", right: "🇺🇸 Gas/Gasoline" }
          ],
          a: "completed",
          successtip: "✓CAR VOCABULARY!\n🚗 Cars = vocabulary goldmine!\n🇬🇧 British car terms different\n🇺🇸 American simplified\nBoth understand with context 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 7: SPELLING DIFFERENCES (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s7",
      title: "Set 7: UK 🇬🇧 vs US 🇺🇸 Spelling",
      questions: [
        {
          type: "match",
          q: "Match spelling patterns",
          pairs: [
            { left: "🇬🇧 -our", right: "🇺🇸 -or" },
            { left: "🇬🇧 -ise", right: "🇺🇸 -ize" },
            { left: "🇬🇧 -re", right: "🇺🇸 -er" }
          ],
          a: "completed",
          successtip: "✓PATTERNS!\nSpelling = systematic differences 📝\nNOT mistakes! ✓\nBoth 100% correct ✓✓\nDepends on your English! 🌍"
        },
        {
          type: "mc",
          q: "🇬🇧 'Colour' = 🇺🇸 ___",
          options: ["Color", "Colour", "Culer"],
          a: "Color",
          tip: "-our → -or pattern",
          successtip: "✓COLOUR/COLOR!\n🇬🇧 colour, favour, honour\n🇺🇸 color, favor, honor\nUS = simplified 🇺🇸\nUK = traditional 🇬🇧\n🇨🇦🇦🇺 = UK spelling! 🍁🦘"
        },
        {
          type: "order",
          q: "Context: 🇬🇧 UK spelling. Traduce: Me doy cuenta de que...",
          words: ["I", "realise", "that..."],
          a: "I realise that...",
          tip: "🇬🇧 -ise ending",
          successtip: "✓REALISE!\n🇬🇧 realise, organise, recognise\n🇺🇸 realize, organize, recognize\nBoth correct! ✓✓\nAutocorrect knows your region 📱"
        },
        {
          type: "input",
          q: "🇺🇸 US spelling: 'cent___' (centro)",
          a: "er",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓CENTER!\n🇬🇧 centre, theatre, metre\n🇺🇸 center, theater, meter\n-re → -er pattern\nUS phonetic spelling 🇺🇸"
        },
        {
          type: "mc",
          q: "🇬🇧 'Defence' = 🇺🇸 ___",
          options: ["Defense", "Defence", "Defens"],
          a: "Defense",
          successtip: "✓DEFENCE/DEFENSE!\n🇬🇧 defence, licence (noun)\n🇺🇸 defense, license\n-ce → -se pattern\nOffence/Offense same! ⚽"
        },
        {
          type: "order",
          q: "Context: 🇺🇸 American writing. Traduce (US): Viajé a Europa.",
          words: ["I", "traveled", "to", "Europe."],
          a: "I traveled to Europe.",
          tip: "🇺🇸 Single L",
          successtip: "✓TRAVELED!\n🇬🇧 travelled (double L)\n🇺🇸 traveled (single L)\nSame with: cancelled/canceled\nUK = double consonant 🇬🇧"
        },
        {
          type: "mc",
          q: "Which follows UK 🇬🇧 spelling?",
          options: ["🇨🇦 Canada and 🇦🇺 Australia", "Only 🇺🇸 USA", "🇨🇦 Canada only"],
          a: "🇨🇦 Canada and 🇦🇺 Australia",
          successtip: "✓COMMONWEALTH!\n🇬🇧 UK spelling:\n  • 🇬🇧 Britain\n  • 🇨🇦 Canada\n  • 🇦🇺 Australia\n  • 🇳🇿 New Zealand\n  • 🇮🇪 Ireland\n🇺🇸 US spelling:\n  • 🇺🇸 USA only!"
        },
        {
          type: "input",
          q: "🇬🇧 'Analyse' = 🇺🇸 Analy___",
          a: "ze",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ANALYZE!\n🇬🇧 analyse, paralyse\n🇺🇸 analyze, paralyze\n-yse → -yze pattern\nZ = American love ❤️🇺🇸"
        },
        {
          type: "mc",
          q: "In academic writing:",
          options: ["Pick one system and stay consistent", "Mix them freely", "Always use US"],
          a: "Pick one system and stay consistent",
          successtip: "✓CONSISTENCY!\nPick one: 🇬🇧 or 🇺🇸 ✓\nDon't mix! ❌\nProfessors notice 👀\nWord processor settings 💻"
        },
        {
          type: "match",
          q: "Match more spelling differences",
          pairs: [
            { left: "🇬🇧 Grey", right: "🇺🇸 Gray" },
            { left: "🇬🇧 Catalogue", right: "🇺🇸 Catalog" },
            { left: "🇬🇧 Programme", right: "🇺🇸 Program" }
          ],
          a: "completed",
          successtip: "✓MORE DIFFERENCES!\nGrey/Gray = both OK everywhere now 🎨\nCatalogue/Catalog = UK keeps -ue\nProgramme/Program = UK TV, US all\n🇬🇧 UK = longer, traditional\n🇺🇸 US = shorter, simplified"
        }
      ]
    },

    // ===================================================================
    // SET 8: CANADA 🇨🇦 & AUSTRALIA 🇦🇺 (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s8",
      title: "Set 8: Canadian 🇨🇦 & Aussie 🇦🇺 English",
      questions: [
        {
          type: "match",
          q: "Match countries to features",
          pairs: [
            { left: "🇨🇦 Canada", right: "UK spelling + US vocab + 'eh?'" },
            { left: "🇦🇺 Australia", right: "UK spelling + unique slang + casual" },
            { left: "🇺🇸 USA", right: "Simplified spelling + direct" }
          ],
          a: "completed",
          successtip: "✓UNIQUE ENGLISHES!\n🇨🇦 = UK/US hybrid 🍁\n🇦🇺 = UK base + own universe 🦘\n🇺🇸 = its own thing 🦅\n🇬🇧 = original 👑\nAll valid! 🌍"
        },
        {
          type: "mc",
          q: "Canadian tag question:",
          options: ["eh?", "innit?", "right?"],
          a: "eh?",
          tip: "EH = Canadian classic!",
          successtip: "✓EH!\n'Nice day, eh?' 🇨🇦\n= British tag questions 🇬🇧\n= American 'right?' 🇺🇸\nCanadian stereotype TRUE! 😅\nSaid constantly! ✓"
        },
        {
          type: "order",
          q: "Context: 🇨🇦 Ordering coffee at Tim Hortons. Traduce: Un doble-doble, por favor.",
          words: ["A", "double-double,", "please."],
          a: "A double-double, please.",
          tip: "Canadian coffee order",
          successtip: "✓DOUBLE-DOUBLE!\n2 cream + 2 sugar ☕\nTim Hortons = Canadian icon 🇨🇦\nLike Starbucks in US 🇺🇸\nCultural institution! 🍁"
        },
        {
          type: "input",
          q: "🇨🇦 Canadian winter hat: ___",
          a: "toque",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓TOQUE!\nToque = winter hat 🧢❄️\nPronounced 'tuke' 🗣️\nOnly Canada says this! 🇨🇦\n🇺🇸 US = beanie\n🇬🇧 UK = woolly hat"
        },
        {
          type: "mc",
          q: "🇨🇦 'Washroom' = 🇺🇸 ___",
          options: ["Restroom / Bathroom", "Toilet", "WC"],
          a: "Restroom / Bathroom",
          successtip: "✓WASHROOM!\n🇨🇦 Washroom (polite)\n🇺🇸 Restroom/Bathroom\n🇬🇧 Toilet/Loo\n🇦🇺 Toilet/Dunny\nSame place, many names! 🚽"
        },
        {
          type: "order",
          q: "Context: 🇦🇺 Australian afternoon. Traduce (Aussie): Nos vemos esta tarde.",
          words: ["See", "ya", "this", "arvo."],
          a: "See ya this arvo.",
          tip: "ARVO = afternoon (Aussie slang)",
          successtip: "✓ARVO!\nArvo = afternoon 🇦🇺\nAussies shorten EVERYTHING\nAdd -o or -ie ending\nBrekkie, servo, bottle-o, arvo...\nUnique language feature! 😎"
        },
        {
          type: "mc",
          q: "🇦🇺 'Brekkie' = ___",
          options: ["Breakfast", "Break", "Snack"],
          a: "Breakfast",
          successtip: "✓BREKKIE!\nBreakfast → Brekkie 🍳\nPattern: shorten + add -ie\nChristmas → Chrissie 🎄\nMcDonald's → Macca's 🍔\nVery Aussie! 🇦🇺🦘"
        },
        {
          type: "input",
          q: "🇦🇺 'G'day ___!' (classic Aussie greeting)",
          a: "mate",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓MATE!\n'G'day mate!' = Hello friend! 🇦🇺\nG'day = Good day 👋\nMate = friend/dude 🤝\nIconic Australian 🦘\nEveryone = mate! 😎"
        },
        {
          type: "mc",
          q: "🇦🇺 'Servo' = ___",
          options: ["Gas/petrol station", "Server", "Service"],
          a: "Gas/petrol station",
          successtip: "✓SERVO!\nService station → Servo ⛽\n🇦🇺 Bottle-o = liquor store 🍺\n🇦🇺 Ute = pickup truck 🚙\n🇦🇺 No worries = you're welcome 👍\nSlang dictionary needed! 📖"
        },
        {
          type: "match",
          q: "Match unique vocabulary",
          pairs: [
            { left: "🇨🇦 Loonie", right: "$1 coin" },
            { left: "🇨🇦 Parkade", right: "Parking garage" },
            { left: "🇦🇺 Dunny", right: "Toilet (outback)" }
          ],
          a: "completed",
          successtip: "✓UNIQUE WORDS!\n🇨🇦 Loonie ($1) / Toonie ($2) 💰\n🇨🇦 Parkade (parking structure) 🅿️\n🇦🇺 Dunny (toilet, old-fashioned) 🚽\nEach country = own gems! 💎🌍"
        }
      ]
    },

    // ===================================================================
    // SET 9: CULTURAL COMMUNICATION STYLES (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s9",
      title: "Set 9: Communication Styles",
      questions: [
        {
          type: "match",
          q: "Match directness levels",
          pairs: [
            { left: "Most direct", right: "🇺🇸 USA" },
            { left: "Very casual", right: "🇦🇺 Australia" },
            { left: "Most indirect", right: "🇬🇧 UK" }
          ],
          a: "completed",
          successtip: "✓DIRECTNESS SCALE!\n🇺🇸 USA = efficient, direct 💼\n🇦🇺 AUS = casual, mate-ly 😎\n🇨🇦 CAN = polite, apologetic 🍁\n🇬🇧 UK = indirect, polite ☕\nAll work! Just different 🌍"
        },
        {
          type: "mc",
          q: "American 🇺🇸 saying no directly:",
          options: ["'I can't make it.'", "'I'm terribly sorry, but I'm afraid I might not be able to attend.'", "'Yeah nah.'"],
          a: "'I can't make it.'",
          tip: "Americans value directness",
          successtip: "✓AMERICAN DIRECT!\n🇺🇸 Direct = efficient ✓\nNot rude = cultural norm 💼\nSave time ⏰\nVs British = too blunt 😬\nContext = country! 🌍"
        },
        {
          type: "order",
          q: "Context: 🇬🇧 British polite disagreement. Traduce: Entiendo tu punto, pero...",
          words: ["I", "see", "your", "point,", "but..."],
          a: "I see your point, but...",
          tip: "British softening technique",
          successtip: "✓BRITISH POLITENESS!\n🇬🇧 Never directly disagree ☕\nSoften with: 'I see what you mean, but...'\n'Interesting, however...'\n'Perhaps, though...'\nIndirectness = art form! 🎨"
        },
        {
          type: "input",
          q: "🇨🇦 Canadian saying sorry even when bumped INTO: '___!'",
          a: "Sorry",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓SORRY!\nCanadians apologize for EVERYTHING 🇨🇦\nYou bump them? They say sorry! 😂\nCultural reflex 🍁\nStereotype = TRUE ✓\nPoliteness extreme! 💯"
        },
        {
          type: "mc",
          q: "🇦🇺 'Yeah nah' means:",
          options: ["No (casual disagreement)", "Yes", "Maybe"],
          a: "No (casual disagreement)",
          successtip: "✓YEAH NAH!\n🇦🇺 Aussie logic 😂:\n'Yeah nah' = No\n'Nah yeah' = Yes\n'Yeah nah yeah' = Yes!\nConfusing but authentic 🦘\nSuper casual 😎"
        },
        {
          type: "order",
          q: "Context: 🇺🇸 American request (direct). Traduce: ¿Puedes enviarme eso?",
          words: ["Can", "you", "send", "me", "that?"],
          a: "Can you send me that?",
          tip: "Direct = American norm",
          successtip: "✓DIRECT REQUEST!\n🇺🇸 'Can you...' = normal 💼\nNot rude in US ✓\n🇬🇧 British prefer: 'Would you mind...?' ☕\nSame request, different style 🌍"
        },
        {
          type: "mc",
          q: "British 🇬🇧 'I hear what you're saying' often means:",
          options: ["'I disagree but being polite'", "'I totally agree'", "'I'm confused'"],
          a: "'I disagree but being polite'",
          successtip: "✓BRITISH CODE! 🇬🇧\n'I hear what you're saying' = I disagree 😬\n'With respect...' = You're wrong 🤦\n'Quite good' = Amazing 🌟\n'Not bad' = Excellent ✓\nUnderstatement masters! ☕"
        },
        {
          type: "input",
          q: "🇦🇺 Aussie greeting: 'How ya ___?' (going)",
          a: "goin'",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["going"] },
          successtip: "✓HOW YA GOIN'!\n🇦🇺 'How ya goin'?' = How are you? 🦘\nVery casual 😎\n'Good, mate!' = answer\nRelaxed culture 🏖️\nNo stress! ✓"
        },
        {
          type: "mc",
          q: "Queue/line culture most sacred in:",
          options: ["🇬🇧 UK (cutting = crime!)", "🇺🇸 USA", "🇦🇺 Australia"],
          a: "🇬🇧 UK (cutting = crime!)",
          successtip: "✓QUEUE! 🇬🇧\nBritish queue culture = SACRED 🚶‍♂️🚶‍♀️\nCutting queue = mortal sin 😤\nWill glare/tut 👀\nOrderly queues everywhere ✓\nCultural identity! 🇬🇧☕"
        },
        {
          type: "match",
          q: "Match greetings",
          pairs: [
            { left: "🇺🇸 USA", right: "Hi! How are you?" },
            { left: "🇬🇧 UK", right: "Alright?" },
            { left: "🇦🇺 Australia", right: "G'day mate!" }
          ],
          a: "completed",
          successtip: "✓GREETINGS!\n🇺🇸 'How are you?' = not real question 😅\n🇬🇧 'Alright?' = rhetorical ☕\n🇨🇦 'How's it going, eh?' 🍁\n🇦🇺 'G'day mate!' = iconic 🦘\nDon't answer deeply! 💬"
        }
      ]
    },

    // ===================================================================
    // SET 10: GLOBAL ENGLISH MASTERY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u12-s10",
      title: "Set 10: Global Integration",
      questions: [
        {
          type: "mc",
          q: "British colleague: 'You're coming to the meeting, aren't you?' This is:",
          options: ["Tag question expecting confirmation", "Real uncertain question", "Statement"],
          a: "Tag question expecting confirmation",
          tip: "British love tag questions!",
          successtip: "✓TAG + CULTURE!\n🇬🇧 Tags = everywhere ☕\nAren't you? Isn't it? Don't they?\nSeeking agreement 🤝\nVery British! 💯"
        },
        {
          type: "order",
          q: "Context: 🇺🇸 American boss (direct). Traduce: ¿Puedes enviar eso para fin del día?",
          words: ["Can", "you", "send", "that", "by", "EOD?"],
          a: "Can you send that by EOD?",
          tip: "EOD = End of Day (US business)",
          successtip: "✓ACRONYMS!\n🇺🇸 EOD = End of Day 📅\nCOB = Close of Business 💼\nASAP = As Soon As Possible ⚡\nAmerican business speak 🇺🇸"
        },
        {
          type: "input",
          q: "🇨🇦 Canadian friend: 'Wanna grab Timmies, eh?' Timmies = ___",
          a: "Tim Hortons",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Timmies", "Tim Horton's"] },
          successtip: "✓TIMMIES!\nTim Hortons = Canadian coffee ☕🇨🇦\nLike Starbucks but MORE Canadian 🍁\nDrunk daily 💯\nCultural institution! 🏛️"
        },
        {
          type: "mc",
          q: "🇦🇺 Aussie mate: 'Fancy a barbie this arvo?' Means:",
          options: ["BBQ this afternoon?", "Barbie doll party?", "Bar this evening?"],
          a: "BBQ this afternoon?",
          successtip: "✓BARBIE + ARVO!\n🇦🇺 Barbie = BBQ 🍖\n🇦🇺 Arvo = afternoon 🌤️\nSuper casual Aussie 🦘\n'Fancy' = British influence 🇬🇧\nMixed English! 😎"
        },
        {
          type: "order",
          q: "Context: Email formal internacional. Traduce: Espero que este email te encuentre bien.",
          words: ["I", "hope", "this", "email", "finds", "you", "well."],
          a: "I hope this email finds you well.",
          tip: "Universal formal opening",
          successtip: "✓UNIVERSAL FORMAL!\nWorks everywhere: 🇺🇸🇬🇧🇨🇦🇦🇺\nProfessional opener 💼\nSlightly old-fashioned but safe 🎩\nGlobal English ✓"
        },
        {
          type: "mc",
          q: "Understanding 'flat' (UK) vs 'apartment' (US) shows:",
          options: ["Cross-cultural vocabulary awareness", "One is wrong", "They're completely different things"],
          a: "Cross-cultural vocabulary awareness",
          successtip: "✓AWARENESS!\nSame thing, different words ✓\nUnderstand both = B2 skill 🎯\nNo right/wrong 🌍\nContext clues help 💡\nGlobal citizen! 🌟"
        },
        {
          type: "input",
          q: "When unsure of formality, start ___. (formal/casual)",
          a: "formal",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓SAFETY RULE!\nStart formal 🎩\nCan always go casual ✓\nCan't go back to formal ❌\nSafe strategy 💯\nRead the room 🎯"
        },
        {
          type: "mc",
          q: "The key to global English communication:",
          options: ["Flexibility, awareness, and context-reading", "Always use American English", "Never use slang"],
          a: "Flexibility, awareness, and context-reading",
          successtip: "✓FLEXIBILITY!\nAdapt to: country 🌍, context 🎯, person 🤝\nNo one-size-fits-all ❌\nRead the room 👀\nB2 MASTERY! 💪\nYou're ready! 🌟"
        },
        {
          type: "order",
          q: "Context: Global mindset. Traduce: El inglés tiene muchos sabores.",
          words: ["English", "has", "many", "flavors."],
          a: "English has many flavors.",
          tip: "Celebrating diversity!",
          successtip: "✓FLAVORS! 🍦\n🇺🇸 US English\n🇬🇧 British English\n🇨🇦 Canadian English\n🇦🇺 Australian English\n+ India, Singapore, South Africa...\nAll valid! All beautiful! 🌍✨"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Which shows global English competence?",
          options: ["Understanding 'lift' = 'elevator', adjusting register, using tags appropriately", "Only knowing US English", "Memorizing all slang"],
          a: "Understanding 'lift' = 'elevator', adjusting register, using tags appropriately",
          successtip: "✓UNIT 12 COMPLETE! 🎉🌍🏆\n\nGLOBAL MASTERY:\n✅ Tag questions (aren't you? / isn't it?)\n✅ Register awareness (formal 🎩 → casual 👕)\n✅ UK 🇬🇧 vocabulary (flat, lift, queue)\n✅ US 🇺🇸 vocabulary (apartment, elevator, line)\n✅ Spelling differences (colour/color)\n✅ Canadian 🇨🇦 uniqueness (eh? toque, Timmies)\n✅ Australian 🇦🇺 slang (arvo, barbie, mate)\n✅ Communication styles (direct/indirect)\n✅ Cultural awareness\n✅ Flexibility & adaptation\n\n✅ 150+ GLOBAL VOCABULARY:\n  🏷️ Tag questions: aren't you, isn't it, don't they\n  🎩 Formal: Dear Sir, I would like, Kind regards\n  👕 Casual: Hey, wanna, gonna, see ya\n  🇬🇧 UK: flat, lift, trousers, biscuit, queue\n  🇺🇸 US: apartment, elevator, pants, cookie, line\n  🇨🇦 Canada: eh, toque, washroom, double-double\n  🇦🇺 Australia: arvo, brekkie, servo, mate, G'day\n\n✅ CRITICAL SKILLS:\n  • Context awareness 🎯\n  • Register flexibility 🎚️\n  • Cross-cultural understanding 🌍\n  • Polite communication 🤝\n  • Adaptation ability 💪\n\nPuedes ahora:\n🌍 Navigate English globally\n🎚️ Adjust formality perfectly\n🇬🇧🇺🇸🇨🇦🇦🇺 Understand all varieties\n💬 Use tag questions naturally\n🎩👕 Switch formal ↔️ casual\n📧 Write professional emails\n😎 Understand slang\n🤝 Communicate across cultures\n\n¡B2 GLOBAL CITIZEN! 🌟\n¡English es TU idioma ahora! 💪\n\nMódulo 6: 12/13 units complete! 🚀\n¡UNA MÁS! 🏁✨"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - INTERNATIONAL GROUP CHAT (10 preguntas)
  // ===================================================================
  readingSet: {
    id: "m6-u12-reading",
    title: "Reading Practice - International Friends Chat",
    category: "reading",
    
    questions: [
      // BLOQUE 1: Planning Begins
      {
        type: "passage",
        passage: `**Group Chat: "World Tour Gang" 🌍**
*Members: Emma 🇬🇧 (UK), Jake 🇺🇸 (USA), Sarah 🇨🇦 (Canada), Matt 🇦🇺 (Australia)*

**Tuesday, 10:00 AM**

**Emma 🇬🇧:**
Right, so we're definitely doing this European trip, aren't we? I've been looking at flats we could rent in London for the first week. They're quite expensive, but worth it, I reckon.

**Jake 🇺🇸:**
Sounds awesome! Just to clarify – by "flat" you mean apartment, right? Still getting used to British English lol 😅

**Emma 🇬🇧:**
Haha yes, sorry! Flat = apartment. I forget you lot use different words!

**Sarah 🇨🇦:**
No worries! I understand both since we use a mix in Canada, eh? But yeah, I'm so excited for this trip! I've already booked my flight.

**Matt 🇦🇺:**
G'day everyone! Just woke up (it's like 8 PM here in Aus). Count me in, mates! When are we leaving?`
      },
      {
        type: "mc",
        q: "Emma uses a tag question 'aren't we?' to:",
        options: ["Seek confirmation (she expects yes)", "Ask a real question (she's unsure)", "Make a statement"],
        a: "Seek confirmation (she expects yes)",
        tip: "Tag = expecting agreement"
      },
      {
        type: "mc",
        q: "Jake doesn't understand 'flat' because:",
        options: ["US uses 'apartment' for the same thing", "'Flat' means something different in US", "He's never heard it before"],
        a: "US uses 'apartment' for the same thing",
        successtip: "✓VOCABULARY DIFFERENCE!\n🇬🇧 Flat = 🇺🇸 Apartment\nSame thing! ✓\nCross-cultural moment 🌍"
      },

      // BLOQUE 2: More Planning
      {
        type: "passage",
        passage: `**Wednesday, 2:00 PM**

**Jake 🇺🇸:**
Can someone send me the dates? I need to request time off work ASAP.

**Emma 🇬🇧:**
Sorry Jake, what's ASAP? 😅

**Jake 🇺🇸:**
Oh sorry! As Soon As Possible. We use tons of acronyms at work lol

**Sarah 🇨🇦:**
Haha! I would like to request the dates too, please. I need to book my tickets soon. Sorry for the late reply – was at Timmies!

**Matt 🇦🇺:**
Timmies? 🤔

**Sarah 🇨🇦:**
Tim Hortons! Coffee shop here in Canada. It's huge, eh?

**Matt 🇦🇺:**
Ah right! Like our Macca's. That's what we call McDonald's. We shorten everything here 😂

**Emma 🇬🇧:**
You're all speaking different languages and it's technically all English! This is brilliant 😂`
      },
      {
        type: "mc",
        q: "Sarah uses 'I would like to request' - this register is:",
        options: ["Formal/polite", "Casual", "Rude"],
        a: "Formal/polite",
        tip: "'Would like' = formal",
        successtip: "✓REGISTER!\nSarah = polite Canadian 🇨🇦\nVery formal phrasing 🎩\nCanadian politeness! ✓"
      },
      {
        type: "mc",
        q: "'Macca's' shows Australian tendency to:",
        options: ["Shorten words and add -ie or -o ending", "Use British words", "Speak formally"],
        a: "Shorten words and add -ie or -o ending",
        successtip: "✓AUSSIE SLANG!\nMcDonald's → Macca's 🍔\nBreakfast → Brekkie 🍳\nAfternoon → Arvo 🌤️\nVery Australian! 🇦🇺🦘"
      },

      // BLOQUE 3: Transportation Talk (CONTINUACIÓN)
      {
        type: "passage",
        passage: `**Thursday, 9:00 AM**

**Matt 🇦🇺:**
How are we getting around London? Public transport, yeah?

**Emma 🇬🇧:**
Yes! The Tube is brilliant. That's what we call the underground train system. We can get Oyster cards for everyone.

**Jake 🇺🇸:**
Tube = subway, got it! And what's an Oyster card?

**Emma 🇬🇧:**
It's a transport card for the Tube, buses, everything. Dead easy to use!

**Sarah 🇨🇦:**
Sounds great! In Toronto we call it the subway too, but we also say "the TTC" (Toronto Transit Commission). Dead easy = very easy, right Emma?

**Emma 🇬🇧:**
Spot on, Sarah! British slang, sorry 😅

**Matt 🇦🇺:**
No worries! We say "heaps easy" in Australia. Same thing, different words, eh Sarah? 😂`
      },
      {
        type: "mc",
        q: "Why does Emma explain 'the Tube'?",
        options: ["Cross-cultural awareness - Americans/Canadians/Aussies say 'subway'", "She thinks they're stupid", "It's a secret word"],
        a: "Cross-cultural awareness - Americans/Canadians/Aussies say 'subway'",
        successtip: "✓VOCABULARY AWARENESS!\n🇬🇧 Tube (London)\n🇺🇸 Subway (US)\n🇨🇦 Subway/Metro (Canada)\n🇦🇺 Metro/Train (Australia)\nSame transport! 🚇"
      },

      // BLOQUE 4: Food & Cultural Confusion
      {
        type: "passage",
        passage: `**Friday, 6:00 PM**

**Jake 🇺🇸:**
I'm starving! Wanna grab some food? I could really go for some chips right now.

**Emma 🇬🇧:**
Chips? For dinner? That's a bit odd, innit? 😂

**Jake 🇺🇸:**
What? Why is that weird?? I love chips!

**Matt 🇦🇺:**
Hahaha I think I know what's happening here! Jake means hot chips (what you call crisps, Emma). Emma, you're thinking of actual chips!

**Emma 🇬🇧:**
OH! Right, yes! 
🇬🇧 UK: Chips = hot fries, Crisps = potato chips in a bag
🇺🇸 US: Chips = potato chips in a bag, Fries = hot fries

**Jake 🇺🇸:**
This is so confusing lol! Same words, totally different food!

**Sarah 🇨🇦:**
Welcome to global English! 😂 We should make a translation guide, shouldn't we?

**Matt 🇦🇺:**
Yeah nah, we'll figure it out as we go, mate! Part of the adventure!`
      },
      {
        type: "mc",
        q: "Emma uses 'innit?' - this is:",
        options: ["British casual tag question (= isn't it?)", "American slang", "Australian slang"],
        a: "British casual tag question (= isn't it?)",
        tip: "'Innit' = isn't it (British casual)",
        successtip: "✓INNIT!\n'Innit?' = 'isn't it?' (British casual) 🇬🇧\nLike Canadian 'eh?' 🇨🇦\nCasual tag question ✓\nVery British! ☕"
      },
      {
        type: "mc",
        q: "Matt says 'Yeah nah' meaning:",
        options: ["No (Australian casual disagreement)", "Yes", "Maybe"],
        a: "No (Australian casual disagreement)",
        successtip: "✓YEAH NAH!\nAustralian logic 🇦🇺:\n'Yeah nah' = No 🚫\n'Nah yeah' = Yes ✓\nConfusing but real! 🦘"
      },

      // BLOQUE 5: Register Shift & Final Plans
      {
        type: "passage",
        passage: `**Saturday, 10:00 AM**

**Sarah 🇨🇦:**
Okay everyone! I've created a shared document with our itinerary. Could you please review it when you have a chance?

**Emma 🇬🇧:**
Brilliant, Sarah! Very organised of you! I shall have a look shortly.

**Jake 🇺🇸:**
Thanks Sarah! I'll check it out later.

**Matt 🇦🇺:**
Cheers mate! You're a legend! 🙌

---

**Later: Sarah emails the travel agency (formal)**

*Subject: Booking Inquiry for Group Travel*

Dear Sir/Madam,

I hope this email finds you well. I would like to inquire about group rates for four travelers visiting London from June 15-30. Would you be available to discuss package options at your earliest convenience?

I appreciate your time and assistance with this matter.

Kind regards,
Sarah Chen

---

**Back to group chat:**

**Jake 🇺🇸:**
Wow Sarah, that email is SO formal compared to how you talk here! 😂

**Sarah 🇨🇦:**
Haha I know! But you have to be professional with businesses, don't you? Different contexts require different language!

**Emma 🇬🇧:**
Quite right, Sarah! That's proper business etiquette, that is.

**Matt 🇦🇺:**
Fair dinkum! Sarah's got the right idea. I do the same - casual with mates, formal at work.

**Jake 🇺🇸:**
True! I guess we all code-switch depending on the situation, right?

**Sarah 🇨🇦:**
Exactly! That's what makes us good communicators, eh? 😊`
      },
      {
        type: "mc",
        q: "Sarah's email shows which register?",
        options: ["Very formal/professional", "Casual like the chat", "Too casual for business"],
        a: "Very formal/professional",
        successtip: "✓REGISTER SWITCH!\nChat = casual 👕\nEmail = formal 🎩\nSame person, different context!\nB2 skill! 💪"
      },
      {
        type: "mc",
        q: "Sarah uses a tag question 'don't you?' to:",
        options: ["Seek agreement/confirmation", "Ask a real question", "Make a demand"],
        a: "Seek agreement/confirmation",
        tip: "Tag = seeking confirmation"
      },

      // PREGUNTA FINAL
      {
        type: "mc",
        q: "FINAL: This chat demonstrates:",
        options: ["English has many varieties (UK/US/Canada/Australia) and register matters", "Only one correct way to speak English", "American English is best"],
        a: "English has many varieties (UK/US/Canada/Australia) and register matters",
        successtip: "✓READING COMPLETE! 🎉📖🌍\n\nGLOBAL ENGLISH SEEN:\n\n🇬🇧 BRITISH (Emma):\n  • Vocabulary: flat, tube, chips, crisps\n  • Tags: aren't we? / innit?\n  • Formal: quite, brilliant, shall\n  • Politeness: indirect, softening\n\n🇺🇸 AMERICAN (Jake):\n  • Vocabulary: apartment, subway, fries, chips\n  • Direct communication style\n  • Acronyms: ASAP, lol\n  • Casual: wanna, gonna\n\n🇨🇦 CANADIAN (Sarah):\n  • Mix of UK spelling + US vocab\n  • Very polite/formal\n  • 'Eh?' tag question\n  • Timmies (Tim Hortons)\n  • Apologetic culture\n\n🇦🇺 AUSTRALIAN (Matt):\n  • Slang: Macca's, mate, G'day, arvo\n  • Yeah nah logic\n  • Super casual culture\n  • Abbreviations everywhere\n  • Fair dinkum, no worries\n\nREGISTER AWARENESS:\n✅ Group chat = casual 👕\n✅ Business email = formal 🎩\n✅ Context determines language ✓\n✅ Code-switching = competence 💪\n\nCROSS-CULTURAL SKILLS:\n✅ Understanding different vocabulary\n✅ Explaining cultural references\n✅ Adapting communication\n✅ Humor about differences\n✅ Mutual respect 🤝\n\nKEY VOCABULARY DIFFERENCES:\n🏠 flat vs apartment\n🚇 tube vs subway\n🍟 chips vs fries vs crisps\n☕ Timmies vs coffee shop\n🍔 Macca's vs McDonald's\n\nTAG QUESTIONS:\n🇬🇧 aren't we? / innit?\n🇨🇦 eh?\n🇺🇸 right?\n🇦🇺 yeah?\n\nCOMMUNICATION STYLES:\n🇬🇧 Polite, indirect\n🇺🇸 Direct, efficient\n🇨🇦 Very polite, apologetic\n🇦🇺 Casual, friendly\n\n✨ ALL VALID! ✨\nEnglish = global language 🌍\nMany flavors = all correct ✓\nContext + flexibility = mastery 💯\n\n¡Ya entiendes English mundial! 🌟"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (10 preguntas)
  // ===================================================================
  reviewSet: {
    id: "m6-u12-review",
    title: "Unit Review - Global English Mastery",
    category: "review",
    exercises: [
      {
        type: "mc",
        q: "REVIEW: Tag question pattern - 'You're ready, ___ you?'",
        options: ["aren't", "are", "don't"],
        a: "aren't",
        tip: "Positive → negative tag"
      },
      {
        type: "mc",
        q: "REVIEW: 🇬🇧 'Flat' = 🇺🇸 ___",
        options: ["Apartment", "House", "Room"],
        a: "Apartment",
        tip: "UK vs US vocabulary"
      },
      {
        type: "input",
        q: "REVIEW: Formal email opening: 'Dear ___ Smith,' (Mr./Ms.)",
        a: "Mr.",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Ms.", "Ms", "Mr", "Dr.", "Dr", "Professor"] },
        tip: "Formal = use title"
      },
      {
        type: "mc",
        q: "REVIEW: Most casual register:",
        options: ["'Wanna hang out?'", "'Would you like to meet?'", "'Shall we meet?'"],
        a: "'Wanna hang out?'",
        tip: "Wanna = very casual"
      },
      {
        type: "mc",
        q: "REVIEW: 🇬🇧 'Colour' = 🇺🇸 ___",
        options: ["Color", "Colour", "Culor"],
        a: "Color",
        tip: "-our → -or pattern"
      },
      {
        type: "order",
        q: "REVIEW: Traduce (formal) - Me gustaría solicitar información.",
        words: ["I", "would", "like", "to", "request", "information."],
        a: "I would like to request information."
      },
      {
        type: "mc",
        q: "REVIEW: Canadian tag question:",
        options: ["eh?", "innit?", "mate?"],
        a: "eh?",
        tip: "Eh = Canadian!"
      },
      {
        type: "match",
        q: "REVIEW: Match countries to features",
        pairs: [
          { left: "🇬🇧 UK", right: "Queue, innit, tube" },
          { left: "🇺🇸 USA", right: "Direct, ASAP, subway" },
          { left: "🇦🇺 Australia", right: "Arvo, mate, casual" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "REVIEW: When unsure about formality:",
        options: ["Start formal, can go casual later", "Start casual always", "Use slang immediately"],
        a: "Start formal, can go casual later",
        tip: "Safe strategy = formal first"
      },
      {
        type: "mc",
        q: "FINAL REVIEW: Global English competence means:",
        options: ["Understanding varieties, adjusting register, using tags appropriately", "Only knowing one type of English", "Correcting everyone's English"],
        a: "Understanding varieties, adjusting register, using tags appropriately",
        successtip: "✓UNIT 12 REVIEW COMPLETE! 🎉🌍🏆\n\nMASTERY ACHIEVED:\n✅ Tag questions (aren't you?)\n✅ Register awareness (🎩→👕)\n✅ UK 🇬🇧 vocabulary\n✅ US 🇺🇸 vocabulary  \n✅ Canadian 🇨🇦 features\n✅ Australian 🇦🇺 slang\n✅ Spelling differences\n✅ Communication styles\n✅ Cultural awareness\n✅ Flexibility & adaptation\n\n🌍 GLOBAL CITIZEN!\n💪 B2 Communication Master!\n🎯 Context-aware speaker!\n\nMódulo 6: 12/13 complete! 🚀\n¡ÚLTIMA UNIDAD NEXT! 🏁✨"
      }
    ]
  }
};
